/**
 * IP Geolocation Service
 *
 * Determines approximate user location from IP address using
 * ipapi.co (free tier, no API key required for low-volume usage).
 *
 * @remarks
 * IP geolocation is approximate (~city-level accuracy) and requires
 * no user permission prompt. Used as a best-effort default map center
 * when the user has no stored search or URL search parameter.
 *
 * Falls back gracefully when the request fails or times out.
 */

export interface IpGeolocationResult {
  lat: number;
  lng: number;
  city?: string;
  region?: string;
  country?: string;
}

/**
 * Timeout for IP geolocation request in milliseconds.
 * Short timeout ensures we don't block map initialization.
 */
const REQUEST_TIMEOUT_MS = 3000;

/**
 * localStorage key for persisted IP location cache.
 */
const STORAGE_KEY = "peach_ip_location";

/**
 * Cache TTL in milliseconds (24 hours).
 * IP location doesn't change frequently, so a long TTL is safe.
 */
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Cached IP geolocation result from last successful lookup.
 * Used for instant access when clearing search (avoids async delay).
 */
let cachedLocation: IpGeolocationResult | null = null;

/**
 * Load cached location from localStorage on module init.
 */
function loadCachedLocation(): IpGeolocationResult | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const { data, timestamp } = JSON.parse(stored);
    const age = Date.now() - timestamp;

    if (age > CACHE_TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return data as IpGeolocationResult;
  } catch {
    return null;
  }
}

/**
 * Save location to localStorage with timestamp.
 */
function saveCachedLocation(location: IpGeolocationResult): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ data: location, timestamp: Date.now() })
    );
  } catch {
    // Ignore storage errors (quota, private mode, etc.)
  }
}

// Initialize from localStorage on module load
cachedLocation = loadCachedLocation();

/**
 * Fetch approximate location from IP address.
 *
 * @returns Resolved coordinates or null if unavailable
 *
 * @remarks
 * Returns cached location immediately if available (localStorage persisted).
 * Uses ipapi.co JSON endpoint. Response is roughly city-level accuracy.
 * The AbortController timeout prevents this from blocking map load.
 */
export async function getLocationFromIp(): Promise<IpGeolocationResult | null> {
  // Return cached location immediately if available
  if (cachedLocation) {
    console.log("[IP Geo] Using cached location:", cachedLocation.city);
    return cachedLocation;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch("https://ipapi.co/json/", {
      signal: controller.signal,
    });

    if (!response.ok) return null;

    const data = await response.json();

    const lat = parseFloat(data.latitude);
    const lng = parseFloat(data.longitude);

    if (isNaN(lat) || isNaN(lng)) return null;

    const result: IpGeolocationResult = {
      lat,
      lng,
      city: data.city,
      region: data.region,
      country: data.country_name,
    };

    // Cache in memory and localStorage
    cachedLocation = result;
    saveCachedLocation(result);

    return result;
  } catch {
    // Swallow AbortError and network errors - fallback handled by caller
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Get cached IP location from last successful lookup.
 *
 * @returns Cached coordinates or null if not yet fetched
 *
 * @remarks
 * Use this for instant access (e.g., when clearing search).
 * Falls back to null if getLocationFromIp() hasn't been called yet.
 */
export function getCachedIpLocation(): IpGeolocationResult | null {
  return cachedLocation;
}
